import React, { useEffect, useState } from 'react';
import {
    Dropdown,
    Input,
    Button,
    Icon,
    Form,
    Grid,
    Segment,
    Header,
    Divider,
} from 'semantic-ui-react';
import config from '../../../common/repository/config';
import LocalStorage from '../../../lib/LocalStorage';

import UserRepository from '../../../common/repository/UserRepository';
import constants from '../../../common/constants';
import Result from '../../../common/repository/Result';
import { toast } from '../../common/Toast';


const DailyStatusDownload: React.FC = () => {
    const [tat, setTat] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [downloading, setDownloading] = useState(false);
    const [totalUsers, setTotalUsers] = useState<{ key: string; text: string; value: string }[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | undefined>(undefined);


    const userRepo = new UserRepository();

    const tatOptions = [
        { key: '2', text: '2 Days', value: '2' },
        { key: '4', text: '4 Days', value: '4' },
        { key: '5', text: '5 Days', value: '5' },
    ];

    const handleBulkDownload = async (allocationId: string, fromDate: string, tat: string) => {
        setDownloading(true);
        try {
            const token = LocalStorage.get(LocalStorage.KEY_AUTH_TOKEN);
            if (!token) {
                toast.error('Authentication token is missing.');
                return;
            }

            const response = await fetch(`${config.apiBaseURL}/case/downloadDailyStatus`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-access-token': token,
                },
                body: JSON.stringify({
                    userId: selectedUserId,
                    from: fromDate,
                    tat,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                const message = errorData.message || `Failed to download: ${response.statusText}`;
                toast.error(message);
                return;
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            const disposition = response.headers.get('Content-Disposition');
            let fileName = `Evidence_${allocationId}.zip`;
            if (disposition && disposition.includes('attachment')) {
                const parts = disposition.split(';');
                for (let part of parts) {
                    part = part.trim();
                    if (part.startsWith('filename=')) {
                        fileName = part.substring(9).replace(/['"]/g, '');
                    }
                }
            }

            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('ZIP file downloaded successfully');
        } catch (error) {
            const err = error as Error;
            toast.error(err?.message || 'Download failed. Please try again.');
            console.error('Error in handleBulkDownload:', err);
        } finally {
            setDownloading(false);
        }
    };


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const result = await userRepo.getUsersByRoleTitle({
                    role: constants.roleTitle.ca,
                });

                if (result instanceof Result.Success) {
                    const userOptions = result.data.items.map((user: any) => ({
                        key: String(user.id),
                        text: user.name,
                        value: String(user.id),
                    }));
                    setTotalUsers(userOptions);
                } else {
                    // toast.error(result.message);
                }
            } catch (e) {
                toast.error((e as Error).message || 'Something went wrong');
            }
        };

        fetchUsers();
    }, []);

    return (
        <Segment padded="very" color="teal" raised>
            <Header as="h2" color="teal" textAlign="center">
                <Icon name="file alternate outline" />
                <Header.Content>
                    Daily Status Sheet

                </Header.Content>
            </Header>
            <Divider />
            <Form>
                <Grid stackable columns={4} doubling>
                    <Grid.Column>
                        <Form.Field>
                            <label><strong>Select TAT</strong></label>
                            <Dropdown
                                placeholder="Select TAT"
                                fluid
                                selection
                                options={tatOptions}
                                value={tat}
                                onChange={(e, { value }) => setTat(value as string)}
                            />
                        </Form.Field>
                    </Grid.Column>

                    <Grid.Column>
                        <Form.Field>
                            <label><strong>From Date</strong></label>
                            <Input
                                type="date"
                                fluid
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Form.Field>
                    </Grid.Column>

                    <Grid.Column>
                        <Form.Field>
                            <label><strong>Select User</strong></label>
                            <Dropdown
                                search
                                placeholder="Select User"
                                fluid
                                selection
                                options={totalUsers}
                                value={selectedUserId || ''}
                                onChange={(e, data) => {
                                    setSelectedUserId(String(data.value));
                                }}
                            />
                        </Form.Field>
                    </Grid.Column>

                    <Grid.Column verticalAlign="bottom">
                        <Form.Field>
                            <label>&nbsp;</label>
                            <Button
                                icon
                                labelPosition="left"
                                color="green"
                                loading={downloading}
                                disabled={!tat || !fromDate || !selectedUserId}
                                onClick={() => handleBulkDownload('daily-status', fromDate, tat)}
                                fluid
                            >
                                <Icon name="download" />
                                Download
                            </Button>
                        </Form.Field>
                    </Grid.Column>
                </Grid>
            </Form>
        </Segment>
    );
};

export default DailyStatusDownload;
